import { useState, useCallback, useRef, useEffect } from "react";
import { validateFile } from "../../utils/validation.js";
import { FILE_UPLOAD } from "../../utils/constants.js";
import ErrorMessage from "../common/ErrorMessage.jsx";
import LoadingSpinner from "../common/LoadingSpinner.jsx";

const FileUpload = ({
  label = "Upload File",
  name,
  value = null, // Add value prop for external control
  onChange,
  onBlur,
  accept = FILE_UPLOAD.ALLOWED_TYPES.join(","),
  required = false,
  disabled = false,
  className = "",
  error = null,
  showPreview = true,
  maxSizeMB = FILE_UPLOAD.MAX_SIZE_MB,
  ...props
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Use provided error or local validation error
  const displayError = error || localError;

  const createPreviewUrl = useCallback((file) => {
    if (!file) return null;

    // Only create preview for images
    if (file.type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }

    return null;
  }, []);

  const handleFileSelect = useCallback(
    (file) => {
      setIsLoading(true);

      // Validate file
      const validation = validateFile(file);
      if (!validation.isValid) {
        setLocalError(validation.errors.join(", "));
        setSelectedFile(null);
        setPreviewUrl(null);
        setIsLoading(false);
        return;
      }

      // Clear any previous errors
      setLocalError(null);

      // Set file and create preview
      setSelectedFile(file);
      const preview = createPreviewUrl(file);
      setPreviewUrl(preview);

      // Call parent onChange
      if (onChange) {
        const syntheticEvent = {
          target: {
            name,
            value: file,
            files: [file],
          },
        };
        onChange(syntheticEvent);
      }

      setIsLoading(false);
    },
    [onChange, name, createPreviewUrl],
  );

  const handleInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setLocalError(null);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Call parent onChange with null
    if (onChange) {
      const syntheticEvent = {
        target: {
          name,
          value: null,
          files: [],
        },
      };
      onChange(syntheticEvent);
    }
  }, [onChange, name]);

  const handleBlur = useCallback(
    (e) => {
      if (onBlur) {
        onBlur(e);
      }
    },
    [onBlur],
  );

  const fieldId = `file-${name}`;
  const hasError = Boolean(displayError);
  const hasFile = Boolean(selectedFile);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Reset component when value prop becomes null (external reset)
  useEffect(() => {
    if (value === null && selectedFile !== null) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setLocalError(null);

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [value, selectedFile]);

  return (
    <div className={`file-upload ${className} ${hasError ? "has-error" : ""}`}>
      {label && (
        <label htmlFor={fieldId} className="form-label">
          {label}
          {required && (
            <span className="required-indicator" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      <div className="file-upload-container">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          id={fieldId}
          name={name}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          onBlur={handleBlur}
          required={required}
          disabled={disabled}
          className="file-input-hidden"
          {...props}
        />

        {/* Drop zone */}
        <div
          className={`file-drop-zone ${dragOver ? "drag-over" : ""} ${hasFile ? "has-file" : ""} ${disabled ? "disabled" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          {isLoading ? (
            <LoadingSpinner size="small" message="Processing file..." />
          ) : hasFile ? (
            <div className="file-selected">
              <div className="file-info">
                <span className="file-icon">📄</span>
                <div className="file-details">
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  className="remove-file-btn"
                  aria-label="Remove file"
                  disabled={disabled}
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className="file-upload-prompt">
              <span className="upload-icon">📁</span>
              <p className="upload-text">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p className="upload-hint">
                PDF or Images (JPEG, PNG, GIF), max {maxSizeMB}MB
              </p>
            </div>
          )}
        </div>

        {/* File preview */}
        {showPreview && previewUrl && (
          <div className="file-preview">
            <img
              src={previewUrl}
              alt="File preview"
              className="preview-image"
              onLoad={() => {
                // Image loaded successfully
              }}
              onError={() => {
                setLocalError("Failed to load image preview");
              }}
            />
          </div>
        )}

        {/* PDF preview placeholder */}
        {showPreview &&
          selectedFile &&
          selectedFile.type === "application/pdf" && (
            <div className="file-preview pdf-preview">
              <div className="pdf-placeholder">
                <span className="pdf-icon">📄</span>
                <p>PDF Preview</p>
                <p className="pdf-name">{selectedFile.name}</p>
              </div>
            </div>
          )}
      </div>

      <ErrorMessage message={displayError} className="field-error" />
    </div>
  );
};

export default FileUpload;
